import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, Trash2, AlertCircle, AlertTriangle, CheckCircle, Lock, RefreshCw, Loader,
  Users, UserX, UserPlus, ChevronDown, ChevronRight, Copy, Link2, ShieldAlert, Scale,
} from 'lucide-react';
import { apiGescomExport } from '../../api/client.js';
import Modal from '../Modal.jsx';
import GescomProjectLinkModal from '../GescomProjectLinkModal.jsx';
import PersonPicker from './PersonPicker.jsx';
import DatePicker from '../DatePicker.jsx';
import {
  card, sectionTitle, thStyle, tdStyle, primaryBtn, secondaryBtn, dangerBtn, pill, inputStyle,
  fmtDay, TERMINAL, Counter, JobProgress,
} from './gescomUi.jsx';

/**
 * Flux d'export d'une session Gescom — 3 temps :
 *  1. PÉRIMÈTRE : plage (incluse dans la période de la session, contrôlé
 *     côté serveur), personnes (ou tag) + « Sauf les collaborateurs ».
 *  2. PRÉVISUALISER : compteurs, lignes exportables (repliables, avec leurs
 *     avertissements BCS / projet inactif), lignes bloquées, affaires à lier,
 *     projets Gescom sans BCS.
 *  3. EXÉCUTER : job batch côté backend, suivi SSE, rapport d'erreurs.
 * Tous les jobs sont rattachés à la session (journal + suivi des lignes).
 */
const isoDay = (d) => d.toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoDay(d);
};
const clampIso = (iso, min, max) => (iso < min ? min : iso > max ? max : iso);

/** Raccourcis de plage, rognés à la session ; null si hors session. */
function rangePresets(sessionFrom, sessionTo) {
  const now = new Date(); // jour local (Suisse), pas UTC
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7; // lundi = 0
  const thisMonday = addDays(today, -weekday);
  const clip = (from, to) => {
    if (to < sessionFrom || from > sessionTo) return null;
    return { from: clampIso(from, sessionFrom, sessionTo), to: clampIso(to, sessionFrom, sessionTo) };
  };
  return [
    { key: 'all', label: 'Toute la session', range: { from: sessionFrom, to: sessionTo } },
    { key: 'prevWeek', label: 'Semaine précédente', range: clip(addDays(thisMonday, -7), addDays(thisMonday, -1)) },
    { key: 'week', label: 'Semaine en cours', range: clip(thisMonday, addDays(thisMonday, 6)) },
  ];
}

export default function GescomExportRunner({ session, persons, onJobFinished }) {
  const closed = session.status !== 'OPEN';

  const [selected, setSelected] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const sessionFrom = String(session.from).slice(0, 10);
  const sessionTo = String(session.to).slice(0, 10);
  const [range, setRange] = useState({ from: sessionFrom, to: sessionTo });
  const presets = useMemo(() => rangePresets(sessionFrom, sessionTo), [sessionFrom, sessionTo]);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [showExportable, setShowExportable] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkNotice, setLinkNotice] = useState(null);
  const [confirmKind, setConfirmKind] = useState(null);
  // Lignes à remplacer dans Gescom malgré un écart (choix explicite).
  const [forced, setForced] = useState(() => new Set());

  const [job, setJob] = useState(null);
  const [events, setEvents] = useState([]);
  const [runError, setRunError] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [copied, setCopied] = useState(false);
  const streamRef = useRef(null);
  const logEndRef = useRef(null);

  useEffect(() => () => { streamRef.current?.close?.(); }, []);
  useEffect(() => { logEndRef.current?.scrollIntoView?.({ block: 'end' }); }, [events]);

  const personIds = useMemo(() => selected.map((s) => s.id), [selected]);
  const excludedIds = useMemo(() => excluded.map((s) => s.id), [excluded]);
  const excludedSet = useMemo(() => new Set(excludedIds), [excludedIds]);

  // Changer la plage invalide la prévisualisation : l'envoi doit porter sur
  // la plage effectivement prévisualisée.
  const changeRange = (next) => {
    const from = clampIso(next.from || sessionFrom, sessionFrom, sessionTo);
    const to = clampIso(next.to || sessionTo, from, sessionTo);
    if (from === range.from && to === range.to) return;
    setRange({ from, to });
    setPreview(null);
    setPreviewError(null);
  };

  const scope = () => ({
    from: range.from,
    to: range.to,
    sessionId: session.id,
    personIds: personIds.length ? personIds : undefined,
    excludedPersonIds: excludedIds.length ? excludedIds : undefined,
  });
  // Les remplacements choisis ne partent qu'avec l'envoi, pas la prévisualisation.
  const jobScope = () => ({ ...scope(), forceKeys: [...forced] });

  const pushLog = useCallback((level, text) => {
    setEvents((cur) => [...cur, { ts: Date.now(), level, text }]);
  }, []);

  // `keepOpen` : relance après une liaison Gescom, sans replier les tableaux.
  const doPreview = async ({ keepOpen = false } = {}) => {
    setPreviewLoading(true);
    setPreviewError(null);
    if (!keepOpen) { setShowBlocked(false); setShowExportable(false); setForced(new Set()); }
    try {
      setPreview(await apiGescomExport.preview(scope()));
    } catch (e) {
      setPreviewError(e.message || 'Échec de la prévisualisation');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const launch = async (kind) => {
    setConfirmKind(null);
    setLaunching(true);
    setRunError(null);
    setEvents([]);
    setJob(null);
    setCopied(false);
    streamRef.current?.close?.();
    streamRef.current = null;
    try {
      const res = kind === 'push' ? await apiGescomExport.push(jobScope()) : await apiGescomExport.delete(jobScope());
      const jobId = res?.jobId;
      if (!jobId) throw new Error('Aucun identifiant de job retourné par le serveur.');
      pushLog('info', res.reused
        ? `Un job identique est déjà en cours (${jobId}) : suivi de celui-ci.`
        : `Job ${kind === 'push' ? 'd’export' : 'de suppression'} lancé (${jobId}).`);
      subscribe(jobId);
    } catch (e) {
      setRunError(e.message || 'Échec du lancement du job');
    } finally {
      setLaunching(false);
    }
  };

  const subscribe = (jobId) => {
    setJob({ id: jobId, status: 'PENDING', totals: { total: 0, success: 0, failed: 0, skipped: 0 } });
    const handlePayload = (raw) => {
      let data;
      try { data = JSON.parse(raw); } catch { return; }
      setJob((cur) => ({
        ...(cur ?? {}),
        id: jobId,
        status: data.status ?? cur?.status,
        totals: {
          total: data.total ?? cur?.totals?.total ?? 0,
          success: data.success ?? cur?.totals?.success ?? 0,
          failed: data.failed ?? cur?.totals?.failed ?? 0,
          skipped: data.skipped ?? cur?.totals?.skipped ?? 0,
        },
        processed: data.processed ?? cur?.processed ?? 0,
      }));
      if (data.message) {
        const level = data.failed > 0 || data.status === 'FAILED' ? 'error' : data.status === 'SUCCESS' ? 'success' : 'info';
        pushLog(level, data.message);
      }
      if (data.status && TERMINAL.has(data.status)) {
        streamRef.current?.close?.();
        streamRef.current = null;
        apiGescomExport.job(jobId)
          .then((full) => {
            setJob(full);
            pushLog(
              full.status === 'SUCCESS' ? 'success' : full.status === 'FAILED' ? 'error' : 'info',
              `Terminé : ${full.status} (${full.totals?.success ?? 0} ok, ${full.totals?.failed ?? 0} échec, ${full.totals?.skipped ?? 0} ignoré).`,
            );
            onJobFinished?.();
          })
          .catch(() => {});
      }
    };
    streamRef.current = apiGescomExport.stream(jobId, {
      onEvent: (e) => {
        if (e.event === 'gescom.progress' || e.event === 'gescom.done') handlePayload(e.data);
      },
      onError: (err) => {
        pushLog('error', `Flux interrompu : ${err.message}. Récupération de l’état…`);
        apiGescomExport.job(jobId).then(setJob).catch(() => {});
      },
    });
  };

  const failedItems = useMemo(
    () => (job?.items ?? []).filter((it) => it && (it.ok === false || it.error)),
    [job],
  );

  const copyErrorReport = async () => {
    const report = failedItems
      .map((it) => `${it.personName ?? ''}\t${it.affaireName ?? ''}\t${it.iso ?? ''}\t${it.error ?? 'échec'}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(report || 'Aucune erreur.');
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.alert('Copie impossible. Rapport :\n' + report);
    }
  };

  const counts = preview?.counts ?? null;

  // Affaires à (re)lier : sans projet Gescom, ou numéro inconnu de Gescom.
  const affairesToLink = useMemo(() => {
    const map = new Map();
    for (const row of preview?.blocked ?? []) {
      if (!row.affaireId || (row.projectIssue !== 'missing' && row.projectIssue !== 'unknown')) continue;
      const cur = map.get(row.affaireId) ?? {
        id: row.affaireId, name: row.affaireName ?? row.affaireId, issue: row.projectIssue,
        gescomProjectId: row.gescomProjectId, lines: 0, hours: 0, persons: new Set(),
      };
      cur.lines += 1;
      cur.hours += (row.billableHours ?? 0) + (row.internalHours ?? 0);
      cur.persons.add(row.personName);
      map.set(row.affaireId, cur);
    }
    return [...map.values()].sort((a, b) => b.hours - a.hours);
  }, [preview]);

  // Projets Gescom sans BCS : à compléter dans Gescom, rien à faire ici.
  const bcsProjects = useMemo(() => {
    const map = new Map();
    for (const row of preview?.blocked ?? []) {
      if (row.projectIssue !== 'bcs') continue;
      const k = String(row.gescomProjectId);
      const cur = map.get(k) ?? { gescomProjectId: row.gescomProjectId, affaires: new Set(), lines: 0, hours: 0 };
      cur.affaires.add(row.affaireName);
      cur.lines += 1;
      cur.hours += (row.billableHours ?? 0) + (row.internalHours ?? 0);
      map.set(k, cur);
    }
    return [...map.values()];
  }, [preview]);

  // Collaborateurs absents de la BCS d'un projet client : à ajouter dans Gescom.
  const bcsMembers = useMemo(() => {
    const map = new Map();
    for (const row of preview?.blocked ?? []) {
      if (row.projectIssue !== 'bcs_member') continue;
      const k = String(row.gescomProjectId);
      const cur = map.get(k) ?? { gescomProjectId: row.gescomProjectId, affaireName: row.affaireName, persons: new Map() };
      const pr = cur.persons.get(row.personName) ?? { lines: 0, hours: 0 };
      pr.lines += 1;
      pr.hours += (row.billableHours ?? 0) + (row.internalHours ?? 0);
      cur.persons.set(row.personName, pr);
      map.set(k, cur);
    }
    return [...map.values()];
  }, [preview]);

  // Contrôle avant envoi : écarts avec ce que Gescom contient déjà.
  const conflicts = useMemo(() => (preview?.blocked ?? []).filter((b) => b.gescomConflict), [preview]);
  const outOfSync = preview?.outOfSync ?? [];
  const toggleForced = (key) => setForced((cur) => {
    const n = new Set(cur);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });
  const setManyForced = (keys, on) => setForced((cur) => {
    const n = new Set(cur);
    for (const k of keys) { if (on) n.add(k); else n.delete(k); }
    return n;
  });

  const configured = preview ? preview.configured !== false : true;
  const canRun = !!preview && configured && !launching && !closed;
  const running = job && !TERMINAL.has(job.status);
  const warnCount = counts?.warnings ?? 0;

  return (
    <div>
      {closed ? (
        <div style={{ ...card, display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: 'var(--text)' }}>
          <Lock size={16} style={{ color: 'var(--text-muted)' }} />
          Session close : la prévisualisation reste possible, l’export et la suppression sont désactivés. Rouvrez la session pour agir.
        </div>
      ) : null}

      {/* 1. PÉRIMÈTRE */}
      <div style={card}>
        <h2 style={sectionTitle}><RefreshCw size={16} /> Périmètre</h2>
        <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Période de la session : du {fmtDay(session.from)} au {fmtDay(session.to)}
        </div>

        <div style={{ marginTop: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
            Plage <span style={{ color: 'var(--text-muted)' }}>(toujours incluse dans la période de la session)</span>
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {presets.map((p) => {
              const active = !!p.range && p.range.from === range.from && p.range.to === range.to;
              return (
                <button
                  key={p.key}
                  type="button"
                  style={{ ...pill(active), opacity: p.range ? 1 : 0.4, cursor: p.range ? 'pointer' : 'not-allowed' }}
                  disabled={!p.range}
                  title={p.range ? `Du ${fmtDay(p.range.from)} au ${fmtDay(p.range.to)}` : 'Hors de la période de la session'}
                  onClick={() => p.range && changeRange(p.range)}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, color: 'var(--text-muted)' }}>
            <label>
              Du <DatePicker value={range.from} onChange={(v) => changeRange({ ...range, from: v })} min={sessionFrom} max={range.to} required style={{ ...inputStyle, marginTop: 4 }} />
            </label>
            <label>
              Au <DatePicker value={range.to} onChange={(v) => changeRange({ ...range, to: v })} min={range.from} max={sessionTo} required style={{ ...inputStyle, marginTop: 4 }} />
            </label>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
            Personnes <span style={{ color: 'var(--text-muted)' }}>(optionnel — vide = toutes ; un tag ajoute toute l’équipe)</span>
          </span>
          <PersonPicker
            persons={persons}
            selected={selected}
            onChange={setSelected}
            emptyLabel="Toutes les personnes"
            emptyIcon={<Users size={13} />}
            struck={excludedSet}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
            Sauf les collaborateurs <span style={{ color: 'var(--text-muted)' }}>(optionnel — jamais synchronisés, même s'ils sont sélectionnés au-dessus)</span>
          </span>
          <PersonPicker
            persons={persons}
            selected={excluded}
            onChange={setExcluded}
            emptyLabel="Aucune exclusion"
            emptyIcon={<UserX size={13} />}
            tone="danger"
          />
        </div>

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: previewLoading ? 0.6 : 1 }}
            onClick={() => doPreview()}
            disabled={previewLoading}
          >
            {previewLoading ? <Loader size={14} className="spin" /> : <RefreshCw size={14} />} Prévisualiser
          </button>
          {previewError ? <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 13 }}>{previewError}</div> : null}
        </div>
      </div>

      {/* 2. PRÉVISUALISER */}
      {preview ? (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <h2 style={sectionTitle}><CheckCircle size={16} /> Prévisualisation</h2>
            {preview.periodLocked ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-pill)', padding: '3px 10px' }}>
                <Lock size={13} /> Période verrouillée
              </span>
            ) : null}
          </div>

          {!configured ? (
            <Banner tone="danger" icon={<AlertCircle size={18} />}>
              Gescom non configuré (variables d&apos;environnement manquantes). L&apos;export et la suppression sont indisponibles.
            </Banner>
          ) : null}
          {preview.catalogUnavailable ? (
            <Banner tone="warning" icon={<AlertTriangle size={18} />}>
              Catalogue des projets Gescom illisible : les contrôles BCS et « projet inconnu » n’ont pas été faits. Gescom refusera lui-même les lignes concernées.
            </Banner>
          ) : null}

          <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Counter label="Exportables" value={counts?.exportable ?? 0} color="var(--success)" />
            <Counter label="Avec avertissement" value={warnCount} color={warnCount ? 'var(--warning)' : undefined} />
            <Counter label="Bloquées" value={counts?.blocked ?? 0} color="var(--danger)" />
            <Counter label="Écarts Gescom" value={conflicts.length + outOfSync.length} color={conflicts.length + outOfSync.length ? 'var(--warning)' : undefined} />
            <Counter label="Personnes" value={counts?.persons ?? 0} />
            <Counter label="Affaires" value={counts?.affaires ?? 0} />
          </div>

          {(preview?.alreadyExportedExcluded ?? 0) > 0 ? (
            <p style={mutedP}>{preview.alreadyExportedExcluded} saisie(s) déjà exportée(s) exclue(s) du push.</p>
          ) : null}
          {(preview?.syncDisabledExcluded ?? 0) > 0 ? (
            <p style={mutedP}>{preview.syncDisabledExcluded} saisie(s) de collaborateurs « Non synchronisé » ignorée(s) (jamais exportées).</p>
          ) : null}

          {/* Contrôle avant envoi */}
          <GescomCheckSection
            check={preview.gescomCheck}
            conflicts={conflicts}
            outOfSync={outOfSync}
            forced={forced}
            onToggle={toggleForced}
            onSetMany={setManyForced}
          />

          {/* Lignes exportables — repliées par défaut */}
          <div style={{ marginTop: 22 }}>
            <button
              type="button"
              onClick={() => setShowExportable((v) => !v)}
              disabled={(preview.exportable?.length ?? 0) === 0}
              style={{ ...secondaryBtn, color: 'var(--success)', borderColor: 'var(--success)' }}
            >
              {showExportable ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Lignes exportables ({preview.exportable?.length ?? 0})
              {warnCount > 0 ? <span style={{ color: 'var(--warning)' }}>· {warnCount} avec avertissement</span> : null}
            </button>
            {(preview.exportable?.length ?? 0) === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Aucune ligne exportable sur ce périmètre.</p>
            ) : null}
            {showExportable ? (
              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      <th style={thStyle}>Personne</th>
                      <th style={thStyle}>Affaire</th>
                      <th style={thStyle}>Jour</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Jours fact.</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Jours interne</th>
                      <th style={thStyle}>Points d’attention</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.exportable ?? []).map((row) => (
                      <tr key={row.key} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <td style={tdStyle}>{row.personName}{row.isSubcontractor ? <span style={{ color: 'var(--text-muted)' }}> (ST)</span> : null}</td>
                        <td style={tdStyle}>{row.affaireName}</td>
                        <td style={tdStyle}>{fmtDay(row.iso)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.billableDays ?? 0}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.internalDays ?? 0}</td>
                        <td style={{ ...tdStyle, color: 'var(--warning)' }}>{(row.warnings ?? []).join(' · ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          {linkNotice ? (
            <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--success)' }}>
              <CheckCircle size={15} /> {linkNotice}
            </div>
          ) : null}

          {/* Affaires à lier / relier à un projet Gescom */}
          {affairesToLink.length > 0 ? (
            <div style={{ marginTop: 18, border: '1px solid var(--warning)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text)', marginBottom: 8, flexWrap: 'wrap' }}>
                <Link2 size={15} style={{ color: 'var(--warning)' }} />
                <strong>{affairesToLink.length} affaire{affairesToLink.length > 1 ? 's' : ''} à lier à Gescom</strong>
                <span style={{ color: 'var(--text-muted)' }}>— pour débloquer leurs lignes</span>
              </div>
              {affairesToLink.map((a) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--glass-border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--text)', overflowWrap: 'anywhere' }}>{a.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {a.issue === 'unknown'
                        ? <span style={{ color: 'var(--danger)' }}>Projet Gescom {a.gescomProjectId} inconnu de Gescom · </span>
                        : 'Aucun projet Gescom · '}
                      {a.lines} ligne{a.lines > 1 ? 's' : ''} · {Math.round(a.hours * 100) / 100} h · {[...a.persons].join(', ')}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setLinkNotice(null); setLinkTarget({ id: a.id, name: a.name }); }}
                    title="Choisir le projet Gescom de cette affaire"
                    style={{ ...secondaryBtn, color: 'var(--accent)', borderColor: 'var(--accent)', flexShrink: 0 }}
                  >
                    <Link2 size={13} /> {a.issue === 'unknown' ? 'Relier' : 'Lier à Gescom'}
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {/* Projets Gescom sans BCS — se corrige dans Gescom */}
          {bcsProjects.length > 0 ? (
            <div style={{ marginTop: 18, border: '1px solid var(--danger)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text)', marginBottom: 6, flexWrap: 'wrap' }}>
                <ShieldAlert size={15} style={{ color: 'var(--danger)' }} />
                <strong>{bcsProjects.length} projet{bcsProjects.length > 1 ? 's' : ''} Gescom sans BCS</strong>
                <span style={{ color: 'var(--text-muted)' }}>— Gescom refuse toute imputation tant que la BCS (budget par rôle) n’est pas définie : à compléter dans Gescom.</span>
              </div>
              {bcsProjects.map((p) => (
                <div key={p.gescomProjectId} style={{ padding: '6px 0', borderTop: '1px solid var(--glass-border)', fontSize: 13, color: 'var(--text)' }}>
                  Projet {p.gescomProjectId} — {[...p.affaires].join(', ')}
                  <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}> · {p.lines} ligne{p.lines > 1 ? 's' : ''} · {Math.round(p.hours * 100) / 100} h</span>
                </div>
              ))}
            </div>
          ) : null}

          {/* Collaborateurs absents de la BCS — se corrige dans Gescom */}
          {bcsMembers.length > 0 ? (
            <div style={{ marginTop: 18, border: '1px solid var(--danger)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text)', marginBottom: 6, flexWrap: 'wrap' }}>
                <UserPlus size={15} style={{ color: 'var(--danger)' }} />
                <strong>Collaborateurs à ajouter à la BCS dans Gescom</strong>
                <span style={{ color: 'var(--text-muted)' }}>— sur un projet client, Gescom refuse les imputations d’un collaborateur absent de la BCS du projet.</span>
              </div>
              {bcsMembers.map((p) => (
                <div key={p.gescomProjectId} style={{ padding: '6px 0', borderTop: '1px solid var(--glass-border)', fontSize: 13, color: 'var(--text)' }}>
                  Projet {p.gescomProjectId} — {p.affaireName}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {[...p.persons].map(([name, v]) => `${name} (${v.lines} ligne${v.lines > 1 ? 's' : ''}, ${Math.round(v.hours * 100) / 100} h)`).join(' · ')}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* Lignes bloquées — repliées par défaut */}
          {(preview.blocked?.length ?? 0) > 0 ? (
            <div style={{ marginTop: 22 }}>
              <button
                type="button"
                onClick={() => setShowBlocked((v) => !v)}
                style={{ ...secondaryBtn, color: 'var(--danger)', borderColor: 'var(--danger)' }}
              >
                {showBlocked ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Lignes bloquées ({preview.blocked.length})
              </button>
              {showBlocked ? (
                <div style={{ marginTop: 10, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <th style={thStyle}>Personne</th>
                        <th style={thStyle}>Affaire</th>
                        <th style={thStyle}>Jour</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>H. fact.</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>H. interne</th>
                        <th style={thStyle}>Raisons</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.blocked ?? []).map((row) => (
                        <tr key={row.key} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                          <td style={tdStyle}>{row.personName}</td>
                          <td style={tdStyle}>
                            {row.affaireName}
                            {row.affaireId && (row.projectIssue === 'missing' || row.projectIssue === 'unknown') ? (
                              <button
                                type="button"
                                title="Lier cette affaire à un projet Gescom"
                                onClick={() => { setLinkNotice(null); setLinkTarget({ id: row.affaireId, name: row.affaireName ?? row.affaireId }); }}
                                style={{ marginLeft: 6, padding: 2, border: 'none', background: 'transparent', color: 'var(--accent)', cursor: 'pointer', verticalAlign: 'middle' }}
                              >
                                <Link2 size={13} />
                              </button>
                            ) : null}
                          </td>
                          <td style={tdStyle}>{fmtDay(row.iso)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{row.billableHours ?? 0}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{row.internalHours ?? 0}</td>
                          <td style={{ ...tdStyle, color: 'var(--danger)' }}>{(row.reasons ?? []).join(' · ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}

          <div style={{ marginTop: 22, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              style={{ ...primaryBtn, opacity: canRun ? 1 : 0.5, cursor: canRun ? 'pointer' : 'not-allowed' }}
              onClick={() => setConfirmKind('push')}
              disabled={!canRun}
              title={closed ? 'Session close' : !configured ? 'Gescom non configuré' : undefined}
            >
              <Upload size={14} /> Pousser vers Gescom
            </button>
            <button
              type="button"
              style={{ ...dangerBtn, opacity: canRun ? 1 : 0.5, cursor: canRun ? 'pointer' : 'not-allowed' }}
              onClick={() => setConfirmKind('delete')}
              disabled={!canRun}
              title={closed ? 'Session close' : !configured ? 'Gescom non configuré' : undefined}
            >
              <Trash2 size={14} /> Supprimer de Gescom
            </button>
          </div>
        </div>
      ) : null}

      {/* 3. EXÉCUTER */}
      {job || runError ? (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <h2 style={sectionTitle}>{running ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />} Exécution</h2>
            {job?.id ? (
              <button type="button" style={secondaryBtn} onClick={() => apiGescomExport.job(job.id).then(setJob).catch((e) => setRunError(e.message))}>
                <RefreshCw size={13} /> Rafraîchir
              </button>
            ) : null}
          </div>
          {runError ? <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 13 }}>{runError}</div> : null}
          {job ? (
            <>
              <div style={{ marginTop: 16 }}><JobProgress job={job} /></div>
              <div
                style={{
                  marginTop: 16, maxHeight: 220, overflowY: 'auto', background: 'var(--glass)',
                  border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', padding: 10,
                  fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
              >
                {events.length === 0 ? (
                  <span style={{ color: 'var(--text-muted)' }}>En attente d&apos;événements…</span>
                ) : events.map((e, i) => (
                  <div key={i} style={{ color: e.level === 'success' ? 'var(--success)' : e.level === 'error' ? 'var(--danger)' : 'var(--text)', padding: '1px 0' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{new Date(e.ts).toLocaleTimeString('fr-FR')} </span>
                    {e.text}
                  </div>
                ))}
                <div ref={logEndRef} />
              </div>
              {job.lastError ? <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 13 }}>{job.lastError}</div> : null}
              {TERMINAL.has(job.status) && failedItems.length > 0 ? (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 13.5, color: 'var(--danger)' }}>Rapport d&apos;erreurs ({failedItems.length})</h3>
                    <button type="button" style={secondaryBtn} onClick={copyErrorReport}>
                      <Copy size={13} /> {copied ? 'Copié' : 'Copier'}
                    </button>
                  </div>
                  <p style={mutedP}>Ces lignes restent « En erreur » dans le suivi de la session, d’où on peut les relancer.</p>
                  <div
                    style={{
                      marginTop: 8, maxHeight: 180, overflowY: 'auto', background: 'var(--glass)',
                      border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', padding: 10,
                      fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {failedItems.map((it, i) => (
                      <div key={i} style={{ padding: '2px 0', color: 'var(--danger)' }}>
                        {it.personName || ''} {it.affaireName || ''} {it.iso || ''} — {it.error || 'échec'}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {linkTarget ? (
        <GescomProjectLinkModal
          affaire={linkTarget}
          sessionId={session.id}
          onClose={() => setLinkTarget(null)}
          onLinked={({ libelle }) => {
            setLinkTarget(null);
            setLinkNotice(`« ${linkTarget.name} » est liée au projet Gescom ${libelle}.`);
            void doPreview({ keepOpen: true });
          }}
        />
      ) : null}

      <Modal
        open={confirmKind !== null}
        onClose={() => setConfirmKind(null)}
        title={confirmKind === 'delete' ? 'Confirmer la suppression de Gescom' : 'Confirmer l’export vers Gescom'}
        footer={
          <>
            <button type="button" style={secondaryBtn} onClick={() => setConfirmKind(null)}>Annuler</button>
            {confirmKind === 'delete' ? (
              <button type="button" style={dangerBtn} onClick={() => launch('delete')}><Trash2 size={14} /> Supprimer</button>
            ) : (
              <button type="button" style={primaryBtn} onClick={() => launch('push')}><Upload size={14} /> Pousser</button>
            )}
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text)' }}>
          {confirmKind === 'delete'
            ? 'Cette action retire de Gescom les lignes de la période de la session, pour le périmètre sélectionné.'
            : 'Cette action pousse vers Gescom les lignes exportables de la période de la session, pour le périmètre sélectionné.'}
        </p>
        <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: 'var(--text-muted)' }}>
          <strong style={{ color: 'var(--text)' }}>{counts?.exportable ?? 0}</strong> ligne(s) exportable(s) seront traitées.
          {(counts?.blocked ?? 0) > 0 ? (
            <> Les <strong style={{ color: 'var(--danger)' }}>{Math.max(0, counts.blocked - [...forced].filter((k) => conflicts.some((c) => c.key === k)).length)}</strong> ligne(s) bloquée(s) seront ignorées.</>
          ) : null}
        </p>
        {confirmKind === 'push' && forced.size > 0 ? (
          <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: 'var(--warning)' }}>
            <strong>{forced.size}</strong> jour(s) seront <strong>remplacés dans Gescom</strong> : l’imputation qui s’y trouve sera supprimée puis remplacée par la valeur synolog.
          </p>
        ) : null}
      </Modal>
    </div>
  );
}

const mutedP = { fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, marginBottom: 0 };

function Banner({ tone, icon, children }) {
  const color = tone === 'danger' ? 'var(--danger)' : 'var(--warning)';
  return (
    <div
      style={{
        marginTop: 14, display: 'flex', alignItems: 'center', gap: 10,
        background: `color-mix(in srgb, ${color} 14%, transparent)`,
        border: `1px solid ${color}`, borderRadius: 'var(--radius-md)',
        padding: '12px 16px', color: 'var(--text)', fontSize: 13,
      }}
    >
      <span style={{ color, flexShrink: 0, display: 'inline-flex' }}>{icon}</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Contrôle avant envoi : jours déjà occupés dans Gescom par une imputation que
 * synolog n'a pas envoyée, et jours envoyés puis modifiés / supprimés DANS
 * Gescom. Rien n'y est remplacé sans être coché.
 */
function GescomCheckSection({ check, conflicts, outOfSync, forced, onToggle, onSetMany }) {
  if (!check) return null;
  if (!check.available) {
    return (
      <Banner tone="warning" icon={<AlertTriangle size={18} />}>
        Imputations Gescom illisibles : le contrôle avant envoi n’a pas pu être fait. Un jour déjà occupé dans Gescom tombera en erreur à l’envoi, sans être écrasé.
      </Banner>
    );
  }
  const nothing = conflicts.length === 0 && outOfSync.length === 0;
  const dayTotal = (b, i) => Math.round(((b ?? 0) + (i ?? 0)) * 10000) / 10000;
  return (
    <div style={{ marginTop: 18, border: `1px solid ${nothing ? 'var(--glass-border)' : 'var(--warning)'}`, borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text)', flexWrap: 'wrap' }}>
        <Scale size={15} style={{ color: nothing ? 'var(--success)' : 'var(--warning)' }} />
        <strong>Contrôle avant envoi</strong>
        <span style={{ color: 'var(--text-muted)' }}>
          {nothing ? '— aucun écart avec ce que Gescom contient déjà.' : '— écarts avec ce que Gescom contient déjà.'}
        </span>
      </div>
      {check.identical > 0 ? (
        <p style={{ ...mutedP, marginTop: 6 }}>
          {check.identical} jour(s) déjà identique(s) dans Gescom : simplement marqués comme poussés, rien n’est envoyé.
        </p>
      ) : null}

      {conflicts.length > 0 ? (
        <CheckTable
          title={`${conflicts.length} jour(s) déjà occupé(s) dans Gescom par une autre valeur`}
          hint="Imputation saisie hors synolog (directement dans Gescom, ancien flux Float…). Cochez pour la remplacer par la valeur synolog ; sinon la ligne reste bloquée."
          rows={conflicts.map((c) => ({
            key: c.key, personName: c.personName, affaireName: c.affaireName, iso: c.iso,
            synolog: dayTotal(c.gescomConflict.billableDaysSynolog, c.gescomConflict.internalDaysSynolog),
            gescom: dayTotal(c.gescomConflict.billableDays, c.gescomConflict.internalDays),
            comment: c.gescomConflict.comment,
          }))}
          forced={forced} onToggle={onToggle} onSetMany={onSetMany}
        />
      ) : null}

      {outOfSync.length > 0 ? (
        <CheckTable
          title={`${outOfSync.length} jour(s) envoyé(s) puis modifié(s) ou supprimé(s) dans Gescom`}
          hint="Synolog a envoyé ces jours, mais Gescom ne porte plus la même valeur. Cochez pour remettre la valeur synolog."
          rows={outOfSync.map((l) => ({
            key: l.key, personName: l.personName, affaireName: l.affaireName, iso: l.iso,
            synolog: dayTotal(l.billableDays, l.internalDays),
            gescom: l.gescom?.current ? dayTotal(l.gescom.current.billableDays, l.gescom.current.internalDays) : null,
            comment: l.gescom?.current?.comment ?? '',
          }))}
          forced={forced} onToggle={onToggle} onSetMany={onSetMany}
        />
      ) : null}
    </div>
  );
}

function CheckTable({ title, hint, rows, forced, onToggle, onSetMany }) {
  const keys = rows.map((r) => r.key);
  const all = keys.every((k) => forced.has(k));
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{title}</div>
        <button type="button" style={{ ...secondaryBtn, padding: '3px 10px', fontSize: 12 }} onClick={() => onSetMany(keys, !all)}>
          {all ? 'Tout décocher' : 'Tout cocher'}
        </button>
      </div>
      <p style={{ ...mutedP, marginTop: 4 }}>{hint}</p>
      <div style={{ marginTop: 8, overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
              <th style={{ ...thStyle, width: 28 }} />
              <th style={thStyle}>Personne</th>
              <th style={thStyle}>Affaire</th>
              <th style={thStyle}>Jour</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Synolog</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Gescom</th>
              <th style={thStyle}>Commentaire Gescom</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} style={{ borderBottom: '1px solid var(--glass-border)', cursor: 'pointer' }} onClick={() => onToggle(r.key)}>
                <td style={tdStyle}>
                  <input type="checkbox" checked={forced.has(r.key)} onChange={() => onToggle(r.key)} onClick={(e) => e.stopPropagation()} aria-label="Remplacer dans Gescom" />
                </td>
                <td style={tdStyle}>{r.personName}</td>
                <td style={tdStyle}>{r.affaireName}</td>
                <td style={tdStyle}>{fmtDay(r.iso)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{r.synolog} j</td>
                <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{r.gescom === null ? 'absent' : `${r.gescom} j`}</td>
                <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{r.comment || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
